import * as Accountant from 'app/calcs/Accountant'
import { NEBFPensionCalculations } from 'app/calcs/customers/NEBF'
import { makeNEBFComputer } from 'app/calcs/customers/NEBF/Factory';
import { INEBFPensionInputs, NEBFFormOfPayment } from 'app/calcs/customers/NEBF/interfaces'
import { FormOfPaymentOptions } from 'app/calcs/pensions'
import { AllFormsOfPayment, IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { MaterialInput, RadioGroup } from 'app/components'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import { Infobox } from 'app/components/Infobox';
import MaterialNumber from 'app/components/material/MaterialNumber';
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { dateAtAge, IRetirementPerson, isSamePersonForSSA, Profile } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, ButtonGroup, Col, FormGroup, ListGroup, ListGroupItem, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'

interface INEBFPensionContainerState extends IProfileContainerState {
  headOfHousehold: IRetirementPerson
  monthlyPayout: string

  // these are synced with profile.pension.NEBF
  beginPaymentsAge: moment.Duration
  formOfPayment: SimplePensionFormOfPayment
  currentYearsService: moment.Duration
  hoursWorked: number
}

class NEBFPensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, INEBFPensionContainerState> {
  beginPaymentsAgeNS = 'pension.NEBF.beginPaymentsAge'
  hoursWorkedNS = 'pension.shared.hoursWorked'
  currentYearsServiceNS = 'pension.NEBF.currentYearsService'
  formOfPaymentNS = 'pension.NEBF.formOfPayment'

  state: INEBFPensionContainerState = {
    profile: null,
    headOfHousehold: null,
    formOfPayment: this.getValueForQuestion(this.formOfPaymentNS) || SimplePensionFormOfPayment.singleLifeOnly,
    currentYearsService: this.getValueForQuestion(this.currentYearsServiceNS) || null,
    hoursWorked: this.getValueForQuestion(this.hoursWorkedNS) || 0,
    monthlyPayout: '$0',
    beginPaymentsAge: this.getValueForQuestion(this.beginPaymentsAgeNS) || null
  }

  initCalc = false

  debounceCalculations = _.debounce(() => {
    const person = this.state.headOfHousehold
    const profile = this.state.profile
    const inputs = this.state.profile.pension.NEBF
    const { beginPaymentsAge } = this.state;
    const when = dateAtAge(person, beginPaymentsAge)

    const computer = makeNEBFComputer(this.state.profile)
    computer.setSpouse(profile.getSpouse())
    computer.getMonthlyPensionAmount(person, inputs, when)
      .then((payout) => {
        const monthlyPayout = utils.toCurrency(payout.amount)
        this.setState({ monthlyPayout })
      })

  }, 300)

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info')
      const headOfHousehold = profile.getHeadOfHousehold()

      if (profile.pension.NEBF) {
        this.debounceCalculations();
      }

      this.setState({ headOfHousehold, profile })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: INEBFPensionContainerState) {
    this.updateCalculations(nextState)
  }

  updateCalculations(nextState: INEBFPensionContainerState) {
    const { profile, formOfPayment, headOfHousehold, currentYearsService, beginPaymentsAge } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(headOfHousehold.selectedRetirementAge, 'years')

    const updates = {
      beginPaymentsAge,
      formOfPayment,
      currentYearsService
    }
    const newNEBF = _.defaults({}, updates, profile.pension.NEBF)

    if (!this.initCalc || !_.isEqual(profile.pension.NEBF, newNEBF)) {
      this.initCalc = true
      profile.pension.NEBF = newNEBF
      this.debounceCalculations()
    }
  }

  updateHoursWorked(hoursWorked: number) {
    this.state.profile.pension.shared.hoursWorked = hoursWorked
    this.setStateAndSave({ hoursWorked })
  }

  updatePaymentsAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.NEBF.beginPaymentsAge = beginPaymentsAge
    this.setState({ beginPaymentsAge })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  updateRetirementAge(deltaYears: number) {
    const NEBF = this.state.profile.pension.NEBF
    NEBF.beginPaymentsAge.add(deltaYears, 'years')

    this.markSignificantProfileUpdate(this.state.profile)
  }

  updateYearsOfService(yos: number) {
    const currentYearsService = moment.duration(yos, 'years')
    this.state.profile.pension.NEBF.currentYearsService = currentYearsService
    this.setState({ currentYearsService })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  render() {
    if (!this.state.headOfHousehold) { return null }
    if (!this.state.profile.pension || !this.state.profile.pension.NEBF || !this.state.beginPaymentsAge) { return null }

    const profile = this.state.profile
    const spouseIsValid = profile.spouseIsValid()

    const { currentYearsService } = this.state.profile.pension.NEBF
    let currentYearsServiceNum = null
    let currentYearsServiceISO = ''
    if (moment.isDuration(currentYearsService)) {
      currentYearsServiceNum = currentYearsService.years()
      currentYearsServiceISO = currentYearsService.toISOString()
    }

    const currentFormOfPayment = FormOfPaymentOptions
      .find((fopo) => fopo.value === this.state.formOfPayment)
      .label

    const beginPaymentsAgeText = utils.toNiceAge(this.state.beginPaymentsAge)
    const showSummary = (this.state.monthlyPayout !== '$0')

    const findFop = (e: AllFormsOfPayment) => _.some(FormOfPaymentOptions, (q) => q.value === e && (spouseIsValid || !q.needsSpouse));
    const allowedFormsOfPayment = _.filter([
      NEBFFormOfPayment.lifeOnly,
      SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
      SimplePensionFormOfPayment.seventyFivePercentJointAndSurvivor
    ], findFop)

    return (
      <section className="animated fadeIn">
        <h1>NEBF Pension</h1>
        <h5 className="lead">Please input information related to your NEBF pension.</h5>
        <Row>
          <Col md={6}>

            <FormGroup>
              <input type="hidden" name={this.currentYearsServiceNS}
                accept="text/duration-years"
                value={currentYearsServiceISO} />
              <MaterialNumber placeholder="How many years of NEBF service do you have?"
                formattedNumberMode
                value={currentYearsServiceNum}
                onChange={(e) => this.updateYearsOfService(this.toNumber(e.currentTarget.value))} />
            </FormGroup>

            <FormGroup>
              <MaterialNumber
                anyPlaceholder={
                  <span>How many hours per year will you work?
                    <Infobox>
                      Note that 1800 hours are required to receive a full year of service under the NEBF plan.
                    </Infobox>
                  </span>
                }
                formattedNumberMode
                onChange={(e) => this.updateHoursWorked(this.toNumber(e.currentTarget.value))}
                name={this.hoursWorkedNS}
                value={this.state.hoursWorked} />
            </FormGroup>

            <FormGroup>
              <RetirementAgeSelector min={60} max={70}
                placeholder="What age will you begin payments?"
                selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updatePaymentsAge(a)} />
            </FormGroup>

            <FormGroup>
              <FormOfPayment name={this.formOfPaymentNS}
                defaultValue={this.state.formOfPayment}
                options={allowedFormsOfPayment}
                onChange={(formOfPayment) => this.setStateAndSave({ formOfPayment })} />
            </FormGroup>

          </Col>
          <Col md={6}>
            {showSummary &&
              <h2 className="animated fadeIn">
                If {this.state.headOfHousehold.firstName} begins to take payments at age&nbsp;
              <a>{beginPaymentsAgeText}</a>&nbsp;
                    in the form of&nbsp;
              <a>{currentFormOfPayment}</a>&nbsp;
              the monthly amount will be <a>{this.state.monthlyPayout}</a>.&nbsp;
            </h2>
            }

          </Col>
        </Row>
      </section >
    )
  }
}

const NEBFPensionQuestionsGroup = () => (
  <NEBFPensionContainer
    header="NEBF Pension" icon="" />
)

export {
  NEBFPensionQuestionsGroup,
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Pension/NEBF/index.tsx